import React, { useState } from "react";
import {
  useTable,
  List,
  EditButton,
  ShowButton,
  DeleteButton,
  CreateButton,
} from "@refinedev/antd";
import { Table, Space, Input, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { DELETE_MINUTE_TEMPLATE_MUTATION, MINUTE_TEMPLATE_LIST_QUERY } from "./queries";

interface IMinuteTemplateRow {
  id: string;
  name: string;
  minuteType: string;
  contentType: string;
  version: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const MinuteTemplateList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState<string>("");
  const [minuteTypeFilter, setMinuteTypeFilter] = useState<string>("");
  const [contentTypeFilter, setContentTypeFilter] = useState<string>("");

  const { tableProps } = useTable<IMinuteTemplateRow>({
    resource: "minuteTemplates",
    initialSorter: [{ field: "createdAt", order: "asc" }],
    initialPageSize: 10,
    meta: {
      gqlQuery: MINUTE_TEMPLATE_LIST_QUERY,
    },
    filters: {
      permanent: [
        ...(isActiveFilter
          ? [{ field: "isActive", operator: "eq" as const, value: isActiveFilter === "true" }]
          : []),
        ...(minuteTypeFilter
          ? [{ field: "minuteType", operator: "eq" as const, value: minuteTypeFilter }]
          : []),
        ...(contentTypeFilter
          ? [{ field: "contentType", operator: "eq" as const, value: contentTypeFilter }]
          : []),
      ],
    },
  });

  const dataSource: IMinuteTemplateRow[] = (
    (tableProps as any)?.dataSource || []
  ).filter(
    (row: IMinuteTemplateRow) =>
      !searchTerm ||
      row.name?.toLowerCase?.().includes(searchTerm.toLowerCase())
  );

  return (
    <List
      headerProps={{
        extra: (
          <Space>
            <Input
              placeholder="Search by name..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onPressEnter={(e) => setSearchTerm(e.currentTarget.value)}
              style={{ width: 280 }}
            />
            <Select
              placeholder="Filter by Minute Type"
              value={minuteTypeFilter}
              onChange={setMinuteTypeFilter}
              style={{ width: 180 }}
              allowClear
            >
              <Select.Option value="BID_OPENING">Bid Opening</Select.Option>
              <Select.Option value="EVALUATION_AWARD">Evaluation and Award</Select.Option>
            </Select>
            <Select
              placeholder="Filter by Content Type"
              value={contentTypeFilter}
              onChange={setContentTypeFilter}
              style={{ width: 180 }}
              allowClear
            >
              <Select.Option value="RAW_CONTENT">Raw Content</Select.Option>
              <Select.Option value="DOCUMENT_TEMPLATE">Document Template</Select.Option>
            </Select>
            <Select
              placeholder="Filter by Status"
              value={isActiveFilter}
              onChange={setIsActiveFilter}
              style={{ width: 140 }}
              allowClear
            >
              <Select.Option value="true">Active</Select.Option>
              <Select.Option value="false">Inactive</Select.Option>
            </Select>
            <CreateButton />
          </Space>
        ),
      }}
    >
      <Table {...tableProps} dataSource={dataSource as any} rowKey="id">
        <Table.Column dataIndex="name" title="Template Name" sorter />
        <Table.Column
          dataIndex="minuteType"
          title="Minute Type"
          sorter
          render={(type: string) => type.toLowerCase().replace("_", " ")}
        />
        <Table.Column
          dataIndex="contentType"
          title="Content Type"
          sorter
          render={(type: string) => type.toLowerCase().replace("_", " ")}
        />
        <Table.Column dataIndex="version" title="Version" sorter />
        <Table.Column
          dataIndex="isActive"
          title="Status"
          sorter
          render={(isActive: boolean) => (
            <span style={{ color: isActive ? "#52c41a" : "#ff4d4f" }}>
              {isActive ? "Active" : "Inactive"}
            </span>
          )}
        />
        <Table.Column dataIndex="createdAt" title="Created Date" sorter />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_text: string, record: IMinuteTemplateRow) => (
            <Space>
              <EditButton
                size="small"
                recordItemId={record.id}
                resource="minuteTemplates"
              />
              <DeleteButton
                size="small"
                recordItemId={record.id}
                meta={{ gqlMutation: DELETE_MINUTE_TEMPLATE_MUTATION }}
                resource="minuteTemplates"
              />
              <ShowButton
                size="small"
                recordItemId={record.id}
                resource="minuteTemplates"
              />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
